// src/withGoogleTranslate.js
import React from "react";

const GOOGLE_TRANSLATE_LANGUAGES = "en,es";

const deleteGoogleTranslateCookie = () => {
  if (typeof document === "undefined") return;
  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `googtrans=; expires=${expires}; path=/`;
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length > 1) {
    const rootDomain = parts.slice(-2).join(".");
    document.cookie = `googtrans=; expires=${expires}; path=/; domain=.${rootDomain}`;
  }
};

const setGoogleTranslateCookie = (lang) => {
  if (typeof document === "undefined") return;
  const value = `/pt/${lang}`;
  document.cookie = `googtrans=${value}; path=/`;
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length > 1) {
    const rootDomain = parts.slice(-2).join(".");
    document.cookie = `googtrans=${value}; path=/; domain=.${rootDomain}`;
  }
};

const ensureScript = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("google_translate_script")) return;
  const script = document.createElement("script");
  script.id = "google_translate_script";
  script.type = "text/javascript";
  script.src =
    "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  script.onerror = (error) => {
    console.error("Falha ao carregar o Google Translate:", error);
  };
  document.body.appendChild(script);
};

const initTranslateWidget = () => {
  if (typeof window === "undefined") return;
  if (!window.google || !window.google.translate) return;
  if (window.__googleTranslateInitialized) return;
  try {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: "pt",
        includedLanguages: GOOGLE_TRANSLATE_LANGUAGES,
        autoDisplay: false,
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
      },
      "google_translate_element",
    );
    window.__googleTranslateInitialized = true;
  } catch (e) {
    console.error("Erro ao inicializar Google Translate:", e);
  }
};

const waitForCombo = (timeoutMs = 5000) =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const combo = document.querySelector("select.goog-te-combo");
      if (combo) return resolve(combo);
      if (Date.now() - start > timeoutMs) return resolve(null);
      setTimeout(tick, 100);
    };
    tick();
  });

const restoreOriginal = () => {
  if (typeof document === "undefined") return;
  deleteGoogleTranslateCookie();
  document.documentElement.classList.remove("translated-ltr", "translated-rtl");
  document.documentElement.removeAttribute("lang-translated");
  // Tenta usar o link "Mostrar original" do widget; se não estiver disponível,
  // recarrega a página para limpar o estado.
  const restoreLink = document.querySelector(
    ".goog-te-spinner-pos .goog-te-menu-value",
  );
  if (restoreLink) restoreLink.click();
  if (
    window.__googleTranslateLastLang &&
    window.__googleTranslateLastLang !== "pt"
  ) {
    // Estávamos com a página traduzida e o usuário voltou para PT.
    // Recarregar é a forma mais segura de limpar a tradução do DOM inteiro.
    window.__googleTranslateLastLang = "pt";
    window.location.reload();
  }
};

// Helper exposto globalmente — chamado pelo LanguageSwitcher.
const setGoogleTranslateLanguage = async (lang) => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  if (lang === "pt" || !lang) {
    restoreOriginal();
    return;
  }

  // Garante que o script e o widget estão prontos antes de tentar traduzir.
  setGoogleTranslateCookie(lang);
  ensureScript();
  initTranslateWidget();

  const combo = await waitForCombo();
  if (combo) {
    combo.value = lang;
    combo.dispatchEvent(new Event("change"));
  }
  window.__googleTranslateLastLang = lang;
};

if (typeof window !== "undefined") {
  window.__setGoogleTranslateLanguage = setGoogleTranslateLanguage;
  window.googleTranslateElementInit = initTranslateWidget;
}

const withGoogleTranslate = (WrappedComponent) => {
  const WithGoogleTranslate = (props) => {
    return (
      <>
        <div
          id="google_translate_element"
          // O widget é carregado por demanda quando o usuário escolhe EN/ES.
          // Mantemos o container no DOM mas escondido — nossa UI é o LanguageSwitcher.
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            height: 0,
            overflow: "hidden",
          }}
          aria-hidden="true"
        ></div>
        <WrappedComponent {...props} />
      </>
    );
  };

  return WithGoogleTranslate;
};

export { setGoogleTranslateLanguage };
export default withGoogleTranslate;
